/**
 * Торговые сессии: Азия, Европа, Америка.
 *
 * Крипта торгуется круглосуточно, но не одинаково: у каждой сессии свой
 * характер. Азия часто рисует узкий диапазон, Европа его пробивает,
 * Америка решает, настоящий ли был пробой. Поэтому сессия рисуется не
 * полосой времени, а прямоугольником её хода — от максимума до минимума,
 * — и видно, где кончился диапазон одной и что с ним сделала следующая.
 *
 * Часы — биржевые, местные: Токио 09:00–18:00, Лондон 08:00–17:00,
 * Нью-Йорк 09:30–16:00. Берутся из того же модуля, что сессии на
 * «Главной» (`market/sessions.ts`): там переход на летнее время считается
 * через настоящие часовые зоны, а субботы и воскресенья пропускаются.
 * Своя таблица с зашитыми часами UTC полгода врала бы на час и
 * размечала выходные как обычные дни.
 */

import { timeframeMs, type Timeframe } from '@specter/types';
import { EXCHANGE_CITY, holidayOn, type ExchangeHoliday } from '@specter/rules';
import { EXCHANGE_OF, sessionsBetween, type SessionSpec as MarketSession } from '../market/sessions.js';
import type { Candle } from '../state/store.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import {
  boolParam,
  colorParam,
  daysParam,
  numberParam,
  type IndicatorContext,
  type IndicatorDefinition,
  type IndicatorParams,
} from './types.js';
import { TABLE_PARAM, priceWords, situationRows, type Situation } from './explain.js';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

interface SessionSpec {
  /** Ключ настройки — прежний: сохранённые профили на него и ссылаются. */
  readonly key: string;
  readonly name: string;
  /** Какая это сессия в общем модуле — с её часовой зоной и часами. */
  readonly market: MarketSession['id'];
  readonly color: string;
}

export const SESSIONS: readonly SessionSpec[] = [
  { key: 'asia', name: 'Азия', market: 'asia', color: '#A78BFA' },
  { key: 'europe', name: 'Европа', market: 'london', color: '#22D3EE' },
  { key: 'us', name: 'Америка', market: 'new-york', color: '#F0A63C' },
];

/** Выходной ли день по UTC: в субботу и воскресенье сессий нет. */
function weekendAt(at: number): boolean {
  const day = new Date(at).getUTCDay();
  return day === 0 || day === 6;
}

/**
 * День недели по ISO: 1 — понедельник, 7 — воскресенье.
 *
 * Все три сессии открываются в ту же дату, что и по UTC — Токио в 00:00,
 * Лондон и Нью-Йорк днём, — так что день сессии и день по UTC совпадают.
 */
function isoDay(at: number): number {
  return new Date(at).getUTCDay() || 7;
}

/** Все будни — выбор по умолчанию и для табло, которому нужен «сегодня». */
const WORKDAYS = new Set([1, 2, 3, 4, 5]);

interface Range {
  readonly session: SessionSpec;
  readonly start: number;
  readonly end: number;
  readonly high: number;
  readonly low: number;
  /** Сессия ещё идёт. */
  readonly live: boolean;
  /** Биржа региона сегодня отдыхает или закрывается раньше. */
  readonly holiday: ExchangeHoliday | null;
}

/**
 * Начало самого раннего из последних `days` выбранных дней.
 *
 * Считаются выбранные рабочие дни, а не сутки: «5 дней» в среду раньше
 * означали «с субботы», и из пяти показанных два были пустыми
 * выходными. А если выбран один понедельник, «5» — это пять последних
 * понедельников: видно, как рынок обычно начинает неделю.
 */
function tradingFloor(last: number, days: number, allowed: ReadonlySet<number>): number {
  let day = Math.floor(last / DAY) * DAY;
  let left = Math.max(1, days);
  // Предел — год назад: дальше свечей графика всё равно нет.
  for (let guard = 0; guard < 400; guard += 1) {
    if (!weekendAt(day) && allowed.has(isoDay(day))) left -= 1;
    if (left === 0) return day;
    day -= DAY;
  }
  return day;
}

/**
 * Диапазоны сессий за последние `days` рабочих дней по свечам графика.
 *
 * Выходных тут нет вовсе: Токио, Лондон и Нью-Йорк в субботу и
 * воскресенье закрыты, и «азиатская сессия» воскресенья — это просто
 * ночь, которую нечем отличить от любой другой.
 */
function rangesOf(
  candles: readonly Candle[],
  days: number,
  enabled: readonly SessionSpec[],
  allowed: ReadonlySet<number> = WORKDAYS,
  source: readonly Candle[] = candles,
): Range[] {
  const last = candles[candles.length - 1];
  if (!last || enabled.length === 0 || allowed.size === 0) return [];
  const byMarket = new Map(enabled.map((session) => [session.market, session]));
  const out: Range[] = [];
  for (const span of sessionsBetween(tradingFloor(last.time, days, allowed), last.time)) {
    const session = byMarket.get(span.session.id);
    if (!session || !allowed.has(isoDay(span.open))) continue;
    let high = Number.NEGATIVE_INFINITY;
    let low = Number.POSITIVE_INFINITY;
    // Ход сессии — по самым мелким свечам, какие есть: на 4ч графике по часовым.
    for (const candle of source) {
      if (candle.time < span.open || candle.time >= span.close) continue;
      high = Math.max(high, candle.high);
      low = Math.min(low, candle.low);
    }
    if (!Number.isFinite(high)) continue;
    out.push({
      session,
      start: span.open,
      end: span.close,
      high,
      low,
      live: last.time < span.close && last.time + stepOf(candles) > span.open,
      holiday: span.holiday,
    });
  }
  return out;
}

/** Шаг ряда: разница соседних свечей, по умолчанию час. */
function stepOf(candles: readonly Candle[]): number {
  const a = candles[candles.length - 2];
  const b = candles[candles.length - 1];
  return a && b ? b.time - a.time : HOUR;
}

/**
 * Свечи, по которым считать ход сессий.
 *
 * На 4ч графике сессия — одна-две свечи, и её максимум по ним — это
 * максимум четырёх часов, захвативших и соседнюю сессию. Поэтому там
 * ход берётся по часовым свечам с Core, а хвост после них — по живым
 * свечам графика: часовой ряд загружен один раз и сам не растёт.
 */
function sourceOf(candles: readonly Candle[], context: IndicatorContext): readonly Candle[] {
  if (timeframeMs(context.timeframe) <= HOUR) return candles;
  const hourly = context.series('1H');
  if (!hourly || hourly.length === 0) return candles;
  const edge = hourly[hourly.length - 1]!.time + HOUR;
  const tail = candles.filter((candle) => candle.time >= edge);
  return tail.length > 0 ? [...hourly, ...tail] : hourly;
}

function enabledOf(p: IndicatorParams): SessionSpec[] {
  return SESSIONS.filter((session) => boolParam(p, session.key, true)).map((session) => ({
    ...session,
    color: colorParam(p, `${session.key}Color`, session.color),
  }));
}

/**
 * На каких графиках сессии видны: до 4ч включительно.
 *
 * На 4ч прямоугольник сессии — одна-две свечи шириной, но ход считается
 * по часовым свечам, и границы точные. Старше — сессия уже меньше свечи,
 * и прямоугольник ничего не скажет.
 */
function fits(context: IndicatorContext): boolean {
  return timeframeMs(context.timeframe) <= 4 * HOUR;
}

function shapesOf(candles: readonly Candle[], p: IndicatorParams, context: IndicatorContext): IndicatorShape[] {
  if (!fits(context)) return [];
  const labels = boolParam(p, 'labels', true);
  const source = sourceOf(candles, context);
  return rangesOf(candles, numberParam(p, 'days', 5), enabledOf(p), daysParam(p, 'weekdays', '12345'), source).map((range) => ({
    kind: 'zone' as const,
    from: range.start,
    to: range.live ? null : range.end,
    upper: range.high,
    lower: range.low,
    tone: 'up' as const,
    color: range.session.color,
    midline: false,
    filled: 0,
    weight: 1 as const,
    closed: false,
    transparency: numberParam(p, 'opacity', 70),
    ...(labels ? { label: range.holiday ? `${range.session.name} · праздник` : range.session.name } : {}),
  }));
}

/** «23 сентября». */
function dayMonth(at: number): string {
  return new Date(at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** «среда, 23 сентября» — день, о котором табло. */
function dayWords(at: number): string {
  const weekday = new Date(at).toLocaleDateString('ru-RU', { weekday: 'long', timeZone: 'UTC' });
  return `${weekday}, ${dayMonth(at)}`;
}

/** Время по часам смотрящего; дальше суток — с днём недели. */
function whenWords(at: number, from: number): string {
  const time = new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (at - from < DAY) return `в ${time}`;
  const weekday = new Date(at).toLocaleDateString('ru-RU', { weekday: 'long' });
  return `${weekday === 'вторник' ? 'во' : 'в'} ${weekday.replace(/а$/, 'у')} в ${time}`;
}

/** Первая свеча, закрывшаяся за азиатским диапазоном после Азии. */
interface Break {
  readonly side: 'up' | 'down';
  /** Какая сессия пробила: «Европа», «Америка» или «Рынок» — между сессиями. */
  readonly by: string;
}

function breakOf(source: readonly Candle[], asia: Range, day: readonly Range[]): Break | null {
  for (const candle of source) {
    if (candle.time < asia.end) continue;
    const side = candle.close > asia.high ? 'up' : candle.close < asia.low ? 'down' : null;
    if (!side) continue;
    const us = day.find((range) => range.session.key === 'us');
    const europe = day.find((range) => range.session.key === 'europe');
    const by =
      us && candle.time >= us.start && candle.time < us.end
        ? 'Америка'
        : europe && candle.time >= europe.start && candle.time < europe.end
          ? 'Европа'
          : 'Рынок';
    return { side, by };
  }
  return null;
}

/** Максимум и минимум свечей в промежутке `[from, to)`. */
function extremesOf(candles: readonly Candle[], from: number, to: number): { low: number; high: number } | null {
  let high = Number.NEGATIVE_INFINITY;
  let low = Number.POSITIVE_INFINITY;
  for (const candle of candles) {
    if (candle.time < from || candle.time >= to) continue;
    high = Math.max(high, candle.high);
    low = Math.min(low, candle.low);
  }
  return Number.isFinite(high) ? { low, high } : null;
}

/**
 * Что Америка сделала с утренним диапазоном Европы — одной фразой.
 *
 * Сравнивается с Европой **до открытия Америки**: с 13:30 до 16:00 UTC
 * они торгуют вместе, и полный диапазон Европы уже содержит минимум,
 * который сделала Америка, — сравнивать с ним значило бы сравнить
 * Америку с самой собой. Так же сессии читают и в классике: Нью-Йорк
 * против утра Лондона.
 *
 * Выход за границу — по экстремуму Америки, удержание — по текущей цене.
 * Вернулась внутрь — вынесли стопы, а сил на продолжение не хватило.
 */
function americaVersusEurope(us: Range, morning: { low: number; high: number }, price: number, live: boolean): string {
  const below = us.low < morning.low;
  const above = us.high > morning.high;
  if (below && above) {
    return `Америка ${live ? 'выходит' : 'вышла'} за обе границы утренней Европы (${priceWords(morning.low)} и ${priceWords(morning.high)}) — день широкий и нервный.`;
  }
  if (!below && !above) {
    return `Америка ${live ? 'пока держится' : 'осталась'} внутри утреннего диапазона Европы ${priceWords(morning.low)} – ${priceWords(morning.high)}.`;
  }
  const down = below;
  const edge = down ? morning.low : morning.high;
  const extreme = down ? us.low : us.high;
  const beyond = down ? price < edge : price > edge;
  if (beyond) {
    return `Америка ${live ? 'пробивает' : 'пробила'} и утренний ${down ? 'минимум' : 'максимум'} Европы (${priceWords(edge)}) и ${live ? 'держится' : 'удержалась'} за ним — ${down ? 'давление продолжилось' : 'покупатели дожали'}.`;
  }
  return `Америка сходила ${down ? 'ниже утреннего минимума' : 'выше утреннего максимума'} Европы (${priceWords(edge)}) до ${priceWords(extreme)}, но вернулась обратно — ${down ? 'продавцы' : 'покупатели'} выдыхаются: за пределами Европы цену не удержали.`;
}

/**
 * Табло «Сессий» — по этапам дня.
 *
 * Классика сессий — это цепочка: Азия строит диапазон, Европа его
 * пробивает, Америка решает, настоящий ли был пробой. Раньше табло
 * смотрело на одно — выше или ниже Азии цена, — и вечером, когда Европа
 * и Америка уже прошли, всё ещё обсуждало «пробой Азии», как будто день
 * только начался. Теперь каждый этап говорит своё: пока идёт Азия — какой
 * строится диапазон; в Европу — пробит ли он; в Америку — держится ли
 * пробой; после закрытия Америки — чем кончился день и когда следующая
 * Азия.
 */
function readoutOf(candles: readonly Candle[], p: IndicatorParams, context: IndicatorContext): ReadoutRow[] {
  const last = candles[candles.length - 1];
  if (!last) return [];
  if (!fits(context)) {
    return [{ label: 'Сессии', value: 'видны на 4ч графике и мельче', tone: 'muted' }];
  }
  const source = sourceOf(candles, context);
  // Для выводов — все три сессии, даже скрытые в настройках: без Азии цепочку не прочитать.
  const day = rangesOf(candles, 1, SESSIONS, WORKDAYS, source);
  const shown = new Set(enabledOf(p).map((session) => session.key));
  const weekend = weekendAt(last.time);
  const asia = day.find((range) => range.session.key === 'asia');
  const europe = day.find((range) => range.session.key === 'europe');
  const us = day.find((range) => range.session.key === 'us');
  const price = last.close;
  const dayStart = day[0]?.start ?? last.time;

  /*
   * Праздники — того дня, о котором табло: торгового дня по UTC. Все три
   * сессии открываются в эту дату. Вечером по Москве этот день в Токио
   * уже прошёл — поэтому строка говорит, какой это день, и в прошедшем
   * времени, если сессия той биржи уже закончилась.
   */
  const utc = new Date(dayStart);
  const holidays = weekend
    ? []
    : SESSIONS.flatMap((session) => {
        const exchange = EXCHANGE_OF[session.market];
        const holiday = holidayOn(exchange, utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
        return holiday ? [{ session, holiday, city: EXCHANGE_CITY[exchange].name }] : [];
      });

  const phase = us?.live
    ? 'america'
    : europe?.live
      ? 'europe'
      : asia?.live
        ? 'asia'
        : us
          ? 'after'
          : 'between';
  const nextAsia = sessionsBetween(last.time + 1, last.time + 5 * DAY).find(
    (span) => span.session.id === 'asia' && span.open > last.time,
  );
  const next = nextAsia ? ` Следующая — Азия ${whenWords(nextAsia.open, last.time)}.` : '';

  let situation: Situation;
  if (weekend) {
    situation = {
      title: 'Выходные',
      tone: 'muted',
      text: 'Токио, Лондон и Нью-Йорк закрыты до понедельника. Крипта торгуется, но без них объёмы тоньше: ход выходных часто ложный, и понедельник его переписывает. Ниже — диапазоны пятницы, от них и отталкиваться.',
    };
  } else if (!asia) {
    situation = {
      title: phase === 'after' ? 'День закрыт' : 'Между сессиями',
      tone: 'muted',
      text: 'Азиатского диапазона сегодня нет — ориентиры дня дают диапазоны Европы и Америки ниже.',
    };
  } else if (phase === 'asia') {
    const tokyoOff = holidays.some((entry) => entry.session.key === 'asia' && entry.holiday.kind === 'closed');
    situation = {
      title: 'Идёт Азия',
      tone: 'muted',
      text:
        `Азия строит диапазон дня: сейчас ${priceWords(asia.low)} – ${priceWords(asia.high)}. Обычно это самая спокойная сессия, а её границы становятся ориентирами — Европа часто пробивает одну из них.` +
        (tokyoOff ? ' Сегодня Токио закрыт, диапазон может выйти уже обычного.' : ''),
    };
  } else {
    const where = price > asia.high ? 'up' : price < asia.low ? 'down' : 'inside';
    const broke = breakOf(source, asia, day);
    const above = (side: 'up' | 'down'): string => (side === 'up' ? 'выше' : 'ниже');
    const edge = (side: 'up' | 'down'): string => priceWords(side === 'up' ? asia.high : asia.low);
    const who = (side: 'up' | 'down'): string => (side === 'up' ? 'покупатели' : 'продавцы');
    const span = `${priceWords(asia.low)} – ${priceWords(asia.high)}`;
    const after = phase === 'after';

    if (broke && where === broke.side) {
      const holdWords = after
        ? 'и до конца дня цена за ним удержалась'
        : phase === 'america' && broke.by !== 'Америка'
          ? 'и Америка держит цену за ним'
          : 'и цена пока держится за ним';
      situation = {
        title: after ? `День закрыт ${above(where)} Азии` : `${above(where) === 'выше' ? 'Выше' : 'Ниже'} Азии — держится`,
        tone: where === 'up' ? 'bull' : 'bear',
        text:
          `${broke.by} пробила азиатский диапазон ${where === 'up' ? 'вверх' : 'вниз'} (${edge(where)}), ${holdWords} — ${who(where)} ${after ? 'вели' : 'ведут'} день.` +
          (phase === 'europe' ? ' Решающая проверка — Америка: удержит — пробой настоящий.' : ''),
      };
    } else if (broke && where === 'inside') {
      situation = {
        title: 'Пробой не удержался',
        tone: 'warn',
        text:
          `${broke.by} выводила цену ${above(broke.side)} Азии, но она вернулась в диапазон ${span}. Похоже на вынос стопов: такие дни нередко разворачиваются в обратную сторону.`,
      };
    } else if (broke && where !== 'inside') {
      situation = {
        title: `Разворот: ${above(where)} Азии`,
        tone: where === 'up' ? 'bull' : 'bear',
        text:
          `Сначала ${broke.by.toLowerCase() === 'рынок' ? 'рынок' : broke.by} пробил${broke.by === 'Рынок' ? '' : 'а'} Азию ${broke.side === 'up' ? 'вверх' : 'вниз'}, а теперь цена ушла за противоположную границу (${edge(where)}). Ложный пробой перешёл в движение в обратную сторону.`,
      };
    } else if (where !== 'inside') {
      situation = {
        title: 'Проверяет границу Азии',
        tone: 'warn',
        text: `Цена сейчас ${above(where)} азиатского диапазона (${edge(where)}), но свеча ещё не закрылась за ним. Закроется — это пробой, вернётся — граница устояла.`,
      };
    } else {
      situation = {
        title: after ? 'День в диапазоне Азии' : 'Внутри Азии',
        tone: 'muted',
        text: after
          ? `Ни Европа, ни Америка не вышли из азиатского диапазона ${span} — рынок весь день ждал.`
          : `${phase === 'america' ? 'Ни Европа, ни Америка пока не вышли' : 'Европа пока не вышла'} из азиатского диапазона ${span}. Пробой одной из границ часто задаёт ход дня.`,
      };
    }
  }

  /*
   * Второе звено цепочки: что Америка сделала с диапазоном Европы.
   *
   * Азия → Европа — первое звено, Европа → Америка — второе. Америка
   * часто добивает пробой Европы или, наоборот, выносит её экстремум и
   * возвращается — это и есть ответ «настоящий ли был ход». Без этой
   * фразы табло молчало о самом свежем событии дня.
   */
  const morning = europe && us ? extremesOf(source, europe.start, us.start) : null;
  if (!weekend && asia && morning && us && (phase === 'america' || phase === 'after')) {
    situation = { ...situation, text: `${situation.text} ${americaVersusEurope(us, morning, price, phase === 'america')}` };
  }
  if (!weekend && phase === 'after' && next) situation = { ...situation, text: situation.text + next };

  const rows: ReadoutRow[] = [...situationRows(situation)];
  rows.push({ label: 'День', value: weekend ? `${dayWords(dayStart)} — последний рабочий` : dayWords(dayStart), tone: 'muted' });
  for (const entry of holidays) {
    const range = day.find((item) => item.session.key === entry.session.key);
    // Сессия этой биржи уже прошла — о празднике в прошедшем времени.
    const past = range !== undefined && !range.live;
    const close = entry.holiday.close;
    rows.push({
      label: `Праздник · ${entry.city}`,
      value:
        entry.holiday.kind === 'short' && close
          ? `${entry.holiday.name} — ${past ? 'торги шли' : 'торги'} до ${String(close[0]).padStart(2, '0')}:${String(close[1]).padStart(2, '0')} по времени биржи`
          : `${entry.holiday.name} — биржа ${past ? 'не работала' : 'закрыта'}, объёмы тоньше`,
      tone: 'muted',
    });
  }
  for (const range of day) {
    if (!shown.has(range.session.key)) continue;
    rows.push({
      label: range.session.name + (range.live ? ' · идёт' : ''),
      value: `${priceWords(range.low)} – ${priceWords(range.high)}`,
      tone: 'muted',
    });
  }
  return rows;
}

const LEGEND: readonly ReadoutLegendItem[] = [
  { sample: 'soft', term: 'Прямоугольник', text: 'ход сессии: от её минимума до максимума. Фиолетовый — Азия, бирюзовый — Европа, оранжевый — Америка.' },
  { sample: 'solid', term: 'Открытый справа', text: 'сессия ещё идёт — диапазон может расшириться.' },
  { sample: 'faded', term: 'Пусто в выходные', text: 'в субботу и воскресенье биржи Токио, Лондона и Нью-Йорка закрыты — сессий нет.' },
  { sample: 'faded', term: 'Пропуск в будний день', text: 'праздник у биржи: например, в «серебряную неделю» Токио закрыт, и Азии нет, а Лондон и Нью-Йорк работают.' },
];

export const SESSIONS_INDICATOR: IndicatorDefinition = {
  id: 'sessions',
  name: 'Сессии',
  description: 'Азия, Европа и Америка прямоугольниками хода: видно, где кончился диапазон одной сессии и что с ним сделала следующая.',
  author: 'specter',
  placement: 'overlay',
  params: [
    { key: 'asia', label: 'Азия (Токио 09:00–18:00)', hint: 'Показывать азиатскую сессию — обычно самую спокойную: её диапазон потом пробивают.', type: 'boolean', default: true },
    { key: 'europe', label: 'Европа (Лондон 08:00–17:00)', hint: 'Показывать европейскую сессию — часто она и пробивает азиатский диапазон. Летнее и зимнее время учтены: по UTC сессия сама сдвигается на час.', type: 'boolean', default: true },
    { key: 'us', label: 'Америка (Нью-Йорк 09:30–16:00)', hint: 'Показывать американскую сессию — самую объёмную: она решает, настоящий ли был пробой. Летнее и зимнее время учтены.', type: 'boolean', default: true },
    {
      key: 'weekdays',
      label: 'Дни недели',
      hint: 'Какие дни размечать. Можно оставить один — например, только понедельники: видно, как рынок обычно начинает неделю. Праздники бирж пропускаются сами.',
      type: 'days',
      default: '12345',
      locked: { days: '67', reason: 'биржи Токио, Лондона и Нью-Йорка в выходные закрыты — сессий нет' },
    },
    { key: 'days', label: 'Сколько дней показывать', hint: 'Сколько последних выбранных дней размечать. Выходные и праздники не в счёт: биржи в них закрыты. Больше — видно привычки рынка, но теснее на графике.', type: 'number', default: 5, min: 1, max: 30, step: 1 },
    { key: 'labels', label: 'Подписи', hint: 'Название сессии у правого края её прямоугольника.', type: 'boolean', default: true },
    { key: 'asiaColor', label: 'Цвет Азии', hint: 'Каким цветом рисовать прямоугольник азиатской сессии — самой спокойной, с узким диапазоном.', type: 'color', default: '#A78BFA' },
    { key: 'europeColor', label: 'Цвет Европы', hint: 'Каким цветом рисовать прямоугольник европейской сессии — той, что чаще пробивает Азию.', type: 'color', default: '#22D3EE' },
    { key: 'usColor', label: 'Цвет Америки', hint: 'Каким цветом рисовать прямоугольник американской сессии — самой объёмной за день.', type: 'color', default: '#F0A63C' },
    { key: 'opacity', label: 'Прозрачность заливки, %', hint: 'Больше процент — бледнее заливка, и свечи под ней видно лучше.', type: 'number', default: 70, min: 0, max: 95, step: 5 },
    TABLE_PARAM,
  ],
  outputs: [],
  // На 4ч ход сессий считается по часовым свечам: их и просим у Core.
  timeframesOf: (_p, chart): Timeframe[] => (timeframeMs(chart) > HOUR ? ['1H'] : []),
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesOf,
  readout: readoutOf,
  legend: LEGEND,
  label: () => 'Сессии',
};
